/**
 * ============================================================================
 * SENSHI ACCESSORY CART OVERRIDE (Strap & Belt)
 * ============================================================================
 * Handles custom add-to-cart for standalone Senshi strap/belt products:
 *   - Custom colour swatch + size (belt) selection
 *   - Looks up correct variant ID from colour+size combination
 *   - Optionally adds Embroidery product ($20) with text as property
 *   - Uses Shopify Cart API /cart/add.js for multi-item add
 *   - Triggers theme's cart drawer after success
 *
 * Loaded by senshi-accessory-fields.liquid on strap/belt product pages.
 * ============================================================================
 */
import Cart from 'cart-store';

(function () {
  'use strict';

  // ── Read config ──
  var configEl = document.getElementById('senshi-accessory-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[Senshi] Failed to parse accessory config:', e);
    return;
  }
  if (!config || !config.isSenshiAccessory) return;

  // ── DOM references ──
  var colourSwatches = document.querySelectorAll('#senshi-colour-swatches .senshi-swatch');
  var colourSelectedDisplay = document.getElementById('senshi-colour-selected');
  var sizeButtons = document.querySelectorAll('#senshi-size-options .senshi-size-btn');
  var embToggle = document.getElementById('senshi-embroidery-toggle');
  var embFields = document.getElementById('senshi-embroidery-fields');
  var embTextInput = document.getElementById('senshi-embroidery-text');
  var charCurrent = document.getElementById('senshi-char-current');

  var form = document.querySelector('.js-product-form');
  if (!form) {
    console.warn('[Senshi] Product form not found');
    return;
  }

  // ── English-only regex: A-Z, a-z, 0-9, space, hyphen, apostrophe, period ──
  var ENGLISH_ONLY = /^[A-Za-z0-9 \-'.]*$/;

  function stripNonEnglish(str) {
    return str.replace(/[^A-Za-z0-9 \-'.]/g, '');
  }

  // ── State ──
  var selectedColour = document.querySelector('input[name="senshi-colour"]:checked');
  selectedColour = selectedColour ? selectedColour.value : config.variants[0].option1;

  var selectedSize = null;
  if (config.isBelt) {
    var sizeInput = document.querySelector('input[name="senshi-size"]:checked');
    selectedSize = sizeInput ? sizeInput.value : config.variants[0].option2;
  }

  // ── Find variant by options ──
  function findVariant(colour, size) {
    for (var i = 0; i < config.variants.length; i++) {
      var v = config.variants[i];
      if (v.option1 === colour) {
        if (config.isBelt) {
          if (v.option2 === size) return v;
        } else {
          return v;
        }
      }
    }
    return null;
  }

  // ── Update the form's hidden variant ID ──
  function updateVariantId() {
    var variant = findVariant(selectedColour, selectedSize);
    if (variant) {
      var variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }
    }
    if (config.isBelt) {
      updateSizeAvailability();
    }
  }

  // ── For belts: update which sizes are available for the selected colour ──
  function updateSizeAvailability() {
    sizeButtons.forEach(function (btn) {
      var size = btn.dataset.size;
      var variant = findVariant(selectedColour, size);
      var available = variant && variant.available;

      btn.classList.toggle('senshi-size-btn--soldout', !available);
      var radio = btn.querySelector('input[type="radio"]');
      if (radio) {
        radio.disabled = !available;
      }

      if (!available && selectedSize === size) {
        var firstAvailable = null;
        sizeButtons.forEach(function (b) {
          if (!firstAvailable) {
            var s = b.dataset.size;
            var v = findVariant(selectedColour, s);
            if (v && v.available) firstAvailable = b;
          }
        });
        if (firstAvailable) {
          sizeButtons.forEach(function (b) { b.classList.remove('senshi-size-btn--active'); });
          firstAvailable.classList.add('senshi-size-btn--active');
          firstAvailable.querySelector('input[type="radio"]').checked = true;
          selectedSize = firstAvailable.dataset.size;
        }
      }
    });
  }

  // ── 1. Colour swatch interaction ──
  colourSwatches.forEach(function (swatch) {
    swatch.addEventListener('click', function (e) {
      if (swatch.dataset.available === 'false') {
        e.preventDefault();
        return;
      }
      colourSwatches.forEach(function (s) {
        s.classList.remove('senshi-swatch--active');
      });
      swatch.classList.add('senshi-swatch--active');
      swatch.querySelector('input[type="radio"]').checked = true;
      selectedColour = swatch.dataset.colour;

      if (colourSelectedDisplay) {
        colourSelectedDisplay.textContent = selectedColour;
      }

      updateVariantId();
    });
  });

  // ── 2. Size button interaction (belts) ──
  sizeButtons.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      if (btn.classList.contains('senshi-size-btn--soldout')) {
        e.preventDefault();
        return;
      }
      sizeButtons.forEach(function (b) { b.classList.remove('senshi-size-btn--active'); });
      btn.classList.add('senshi-size-btn--active');
      btn.querySelector('input[type="radio"]').checked = true;
      selectedSize = btn.dataset.size;

      updateVariantId();
    });
  });

  // ── 3. Embroidery toggle ──
  if (embToggle && embFields) {
    embToggle.addEventListener('change', function () {
      embFields.style.display = embToggle.checked ? 'block' : 'none';
      if (!embToggle.checked && embTextInput) {
        embTextInput.value = '';
        if (charCurrent) charCurrent.textContent = '0';
        clearEmbroideryError();
      }
      if (embToggle.checked && embTextInput) {
        embTextInput.focus();
      }
    });
  }

  // ── 4. Real-time: strip non-English characters as customer types ──
  if (embTextInput) {
    embTextInput.addEventListener('input', function () {
      var cursor = embTextInput.selectionStart;
      var original = embTextInput.value;
      var cleaned = stripNonEnglish(original);

      if (cleaned !== original) {
        embTextInput.value = cleaned;
        var removed = original.length - cleaned.length;
        embTextInput.setSelectionRange(cursor - removed, cursor - removed);
        showEmbroideryError('English characters only (A-Z, 0-9, spaces and basic punctuation).');
      } else {
        clearEmbroideryError();
      }

      if (charCurrent) charCurrent.textContent = embTextInput.value.length;
    });

    // Also catch paste events
    embTextInput.addEventListener('paste', function (e) {
      e.preventDefault();
      var pasted = (e.clipboardData || window.clipboardData).getData('text');
      var cleaned = stripNonEnglish(pasted).substring(0, 10);
      var start = embTextInput.selectionStart;
      var end = embTextInput.selectionEnd;
      var current = embTextInput.value;
      var newVal = (current.substring(0, start) + cleaned + current.substring(end)).substring(0, 10);
      embTextInput.value = newVal;
      if (charCurrent) charCurrent.textContent = newVal.length;
      if (pasted !== cleaned) {
        showEmbroideryError('Non-English characters were removed from your paste.');
      } else {
        clearEmbroideryError();
      }
    });
  }

  function showEmbroideryError(msg) {
    var el = document.getElementById('senshi-embroidery-error');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }

  function clearEmbroideryError() {
    var el = document.getElementById('senshi-embroidery-error');
    if (el) { el.style.display = 'none'; }
  }

  // ── 5. Intercept form submission ──
  form.addEventListener(
    'submit',
    function (e) {
      if (!document.getElementById('senshi-accessory-fields')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      handleAccessoryAddToCart();
    },
    true
  );

  var submitBtn = form.querySelector('[type="submit"], button[name="add"]');
  if (submitBtn) {
    submitBtn.addEventListener(
      'click',
      function (e) {
        if (!document.getElementById('senshi-accessory-fields')) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        handleAccessoryAddToCart();
      },
      true
    );
  }

  // ── Set initial variant ID ──
  updateVariantId();

  // ── Main add-to-cart handler ──
  async function handleAccessoryAddToCart() {
    var variant = findVariant(selectedColour, selectedSize);
    if (!variant) {
      showError('Please select available options.');
      return;
    }
    if (!variant.available) {
      showError('This combination is currently sold out.');
      return;
    }

    var embroideryEnabled = embToggle ? embToggle.checked : false;
    var embroideryText = embTextInput ? embTextInput.value.trim() : '';

    // ── Validation ──
    if (embroideryEnabled && embroideryText.length === 0) {
      showError('Please enter your embroidery text.');
      embTextInput.focus();
      return;
    }
    if (embroideryEnabled && embroideryText.length > 10) {
      showError('Embroidery text must be 10 characters or less.');
      embTextInput.focus();
      return;
    }
    if (embroideryEnabled && !ENGLISH_ONLY.test(embroideryText)) {
      showError('Embroidery text must use English characters only.');
      embTextInput.focus();
      return;
    }

    // ── Build items array ──
    var items = [];

    var mainItem = {
      id: variant.id,
      quantity: 1
    };
    if (embroideryEnabled && embroideryText) {
      var embLabel = config.productType === 'belt' ? 'Belt Embroidery' : 'Strap Embroidery';
      mainItem.properties = {};
      mainItem.properties[embLabel] = embroideryText;
    }
    items.push(mainItem);

    if (embroideryEnabled && embroideryText) {
      var productLabel = config.productType === 'belt'
        ? 'Senshi Belt (' + selectedColour + ' / ' + selectedSize + ')'
        : 'Senshi Strap (' + selectedColour + ')';
      items.push({
        id: config.embroideryVariantId,
        quantity: 1,
        properties: {
          'Embroidery Text': embroideryText
        }
      });
    }

    // ── Submit to Cart API ──
    setButtonLoading(true);

    try {
      await Cart.add(items);
      openCartDrawer();
    } catch (err) {
      console.error('[Senshi] Add to cart error:', err);
      showError(err.message || 'Error adding to cart. Please try again.');
    } finally {
      setButtonLoading(false);
    }
  }

  // ── Open the theme's cart drawer (same pattern as product-form.js) ──
  function openCartDrawer() {
    var cartModal = document.querySelector('.modal--cart, .popup--cart-notification');
    if (!cartModal) {
      window.location.href = '/cart';
      return;
    }
    if (!cartModal.shown) {
      cartModal.open();
    } else if (cartModal.interval) {
      cartModal.interval.reset();
      cartModal.interval.start();
    }
  }

  // ── Button loading state ──
  function setButtonLoading(loading) {
    var btn = form.querySelector('[type="submit"], button[name="add"], .js-add-to-cart');
    if (!btn) return;
    if (loading) {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('btn--loading', 'is-loading');
    } else {
      btn.removeAttribute('disabled');
      btn.classList.remove('btn--loading', 'is-loading');
    }
  }

  // ── Error display ──
  function showError(msg) {
    var errorContainer = form.querySelector('.product-form__error, .js-form-error');
    if (errorContainer) {
      errorContainer.textContent = msg;
      errorContainer.style.display = 'block';
      setTimeout(function () { errorContainer.style.display = 'none'; }, 5000);
      return;
    }
    alert(msg);
  }
})();
